import cn from 'classnames';

import { Article, ArticleBasedVoucher, Price, useCheckoutContext } from '@zooplus/checkout-state';
import { P1, Anchor } from '@zooplus/ui-components';

import CartItemQuantityHandler from '../cart-item-quantity-handler';
import styles from '../cart-item.module.scss';
import ItemStandardPrice from '../item-standard-price';

import ItemBasedDiscount from '@components/common/item-based-discount';
import {
  CART_ITEM_IMAGE_DIMENSION,
  CART_VERSION,
  CHECKOUT_STATE_PROJECT_ID,
} from '@utilities/constants';
import { showFallbackImage } from '@utilities/fallback-image';
import { isObjectNotNullOrEmpty, trimLeadingHyphenAndSpaces } from '@utilities/utils';

const PriceLabel = ({
  article,
  isCartApiV2,
  customStyles,
}: {
  article: Article;
  isCartApiV2: boolean;
  customStyles?: string;
}) => (
  <P1 bold className={cn(styles.bonusPointLabel, customStyles)}>
    {isCartApiV2
      ? ((article?.price as Price)?.labelPrefix ?? '')
      : (article.actualPriceLabel ?? '')}
  </P1>
);

const BonusPointItem = (article: Article) => {
  const { cartVersion, mainTogglesMap = {} } = useCheckoutContext(CHECKOUT_STATE_PROJECT_ID);
  const isCartApiV2: boolean = cartVersion === CART_VERSION.V2;
  const { WIDTH, HEIGHT } = CART_ITEM_IMAGE_DIMENSION;
  const isNewPricingEnabled = mainTogglesMap.NEW_PRICING_COMPONENT_IN_CART_AND_PREVIEW;

  const abdApplied = isCartApiV2
    ? article.lineItemDiscounts?.articleBasedDiscount?.applied
    : article.voucherApplied;
  const abdDetails = isCartApiV2
    ? article.lineItemDiscounts?.articleBasedDiscount
    : article.articleBasedVoucher;
  const productLink = `${article.productLink}?activeVariant=${article.shopId}`;

  return (
    <article>
      <section className={styles.productInfoWrapper}>
        <div className={styles.productImgWrapper}>
          <Anchor href={productLink} size="small">
            <img
              loading="lazy"
              src={article.pictureUrl}
              alt={article.name}
              onError={(e) =>
                showFallbackImage(e, {
                  width: WIDTH,
                  height: HEIGHT,
                })
              }
              width={WIDTH}
              height={HEIGHT}
            />
          </Anchor>
        </div>
        <div data-testid="product-info" className={styles.productNameWrapper}>
          <Anchor data-testid="product-name" href={productLink} isBold>
            {article.name}
          </Anchor>
          <P1 data-testid="product-variant" className={styles.productVariantName}>
            {trimLeadingHyphenAndSpaces(article.variantName)}
          </P1>
          {abdApplied && isObjectNotNullOrEmpty(abdDetails) && (
            <ItemBasedDiscount {...(abdDetails as ArticleBasedVoucher)} />
          )}
        </div>

        <div className={styles.productPricingDetails}>
          <div className={styles.priceDetailsWrapper}>
            <div className={styles.innerWrapper}>
              <div className={styles.priceWrapper}>
                <div
                  className={cn(
                    styles.bonusPointWrapper,
                    isNewPricingEnabled && styles.newPriceBPWrapper,
                  )}>
                  <ItemStandardPrice
                    isBonusArticle={true}
                    points={article.bonusPoints}
                    isNewPricingEnabled={isNewPricingEnabled}
                  />
                  <PriceLabel
                    article={article}
                    isCartApiV2={isCartApiV2}
                    customStyles={cn(isNewPricingEnabled && styles.newPriceBPLabel)}
                  />
                </div>
              </div>
              <CartItemQuantityHandler {...article} />
              <div className={styles.productValueAsPerQuantity}>
                <ItemStandardPrice
                  isBonusArticle={true}
                  points={article.grossBonusPoints}
                  isNewPricingEnabled={isNewPricingEnabled}
                />
                <PriceLabel article={article} isCartApiV2={isCartApiV2} />
              </div>
            </div>
          </div>
        </div>
      </section>
    </article>
  );
};

export default BonusPointItem;
